import React, { useEffect, useCallback, useMemo } from 'react';
import {
  publicMarketService,
  CoinActivity,
  MarketCoinPositionRow,
  MarketCoinPositionsPayload,
  MarketEligibleStrategy,
  MarketKucoinAccountOption,
  MarketOpenMode,
  MarketOpenPositionPayload,
  MarketOpenPositionResult,
} from '../../services/publicMarketService';
import { store } from '../../store';
import CoinActivityChart from './CoinActivityChart';

interface CoinPositionsDrawerProps {
  coin: CoinActivity | null;
  open: boolean;
  onClose: () => void;
  isAdminContext: boolean;
  canOpenPositions?: boolean;
  onViewStrategy: (strategyId: number, symbol: string) => void;
  /** When provided, selects the initial open mode (DIRECT vs STRATEGY). */
  defaultOpenMode?: MarketOpenMode;
}

function resolveFetchMode(): 'admin' | 'retail' | null {
  const { auth } = store.getState();
  if (auth.adminUser && auth.token) return 'admin';
  if (auth.isAuthenticated && !auth.adminUser && (auth.token || auth.type === 'SESSION')) return 'retail';
  return null;
}

function fmtPnl(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  const sign = v >= 0 ? '+' : '';
  return `${sign}$${Math.abs(v).toFixed(2)}`;
}

function formatCryptoPrice(n: number | null | undefined, withDollar = false): string {
  const v = Number(n ?? 0);
  if (!v || !Number.isFinite(v)) return '—';

  let formatted: string;
  if (v >= 1000) {
    formatted = v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } else if (v >= 1) {
    formatted = v.toFixed(2);
  } else if (v >= 0.01) {
    formatted = v.toFixed(4);
  } else if (v >= 0.0001) {
    formatted = v.toFixed(6);
  } else {
    formatted = v.toFixed(8);
  }

  formatted = formatted.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');
  return withDollar ? `$${formatted}` : formatted;
}

function fmtPrice(n: number | null | undefined): string {
  return formatCryptoPrice(n, false);
}

function fmtSpotPrice(n: number | null | undefined): string {
  return formatCryptoPrice(n, true);
}

function fmtUsdCompact(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!v || !Number.isFinite(v)) return '—';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
}

type PositionsView = 'coin' | 'all';

const CoinPositionsDrawer: React.FC<CoinPositionsDrawerProps> = ({
  coin,
  open,
  onClose,
  isAdminContext,
  canOpenPositions = false,
  onViewStrategy,
  defaultOpenMode,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [data, setData] = React.useState<MarketCoinPositionsPayload | null>(null);
  const [eligibleStrategies, setEligibleStrategies] = React.useState<MarketEligibleStrategy[]>([]);
  const [directAccounts, setDirectAccounts] = React.useState<MarketKucoinAccountOption[]>([]);
  const [defaultDirectAmount, setDefaultDirectAmount] = React.useState(10);
  const [defaultDirectLeverage, setDefaultDirectLeverage] = React.useState(5);
  const [openMode, setOpenMode] = React.useState<MarketOpenMode>(defaultOpenMode ?? 'STRATEGY');
  const [selectedKucoinConfigId, setSelectedKucoinConfigId] = React.useState<number | ''>('');
  const [directAmount, setDirectAmount] = React.useState('10');
  const [directLeverage, setDirectLeverage] = React.useState('5');
  const [optionsLoading, setOptionsLoading] = React.useState(false);
  const [optionsError, setOptionsError] = React.useState<string | null>(null);
  const [selectedStrategyId, setSelectedStrategyId] = React.useState<number | ''>('');
  const [side, setSide] = React.useState<'LONG' | 'SHORT'>('LONG');
  const [submitting, setSubmitting] = React.useState(false);
  const [closingHedgePositionId, setClosingHedgePositionId] = React.useState<number | null>(null);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = React.useState<string | null>(null);
  const [positionsView, setPositionsView] = React.useState<PositionsView>('coin');
  const [coinProfile, setCoinProfile] = React.useState<CoinActivity | null>(null);

  const displayCoin = useMemo((): CoinActivity | null => {
    if (!coin) return null;
    if (!coinProfile) return coin;
    return {
      ...coin,
      ...coinProfile,
      symbol: coin.symbol,
      active: coinProfile.active ?? coin.active,
      change24h: coinProfile.change24h ?? coin.change24h,
      volume24h: coinProfile.volume24h ?? coin.volume24h,
    };
  }, [coin, coinProfile]);

  const loadPositions = useCallback(async (symbol: string, view: PositionsView) => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const mode = isAdminContext ? 'admin' : resolveFetchMode();
      const payload =
        mode === 'admin'
          ? view === 'all'
            ? await publicMarketService.getAdminAllCoinPositions()
            : await publicMarketService.getAdminCoinPositions(symbol)
          : mode === 'retail'
            ? view === 'all'
              ? await publicMarketService.getRetailAllCoinPositions()
              : await publicMarketService.getRetailCoinPositions(symbol)
            : null;
      if (!payload) {
        setError('Sign in with market signals access to view positions.');
        return;
      }
      setData(payload);
    } catch (err: any) {
      const code = err?.message || '';
      if (code === '401' || code === '403' || code.includes('403') || code.includes('401')) {
        setError('You do not have permission to view open positions.');
      } else {
        setError('Failed to load positions. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [isAdminContext]);

  const loadOpenOptions = useCallback(async (symbol: string) => {
    if (!canOpenPositions) {
      setEligibleStrategies([]);
      setDirectAccounts([]);
      return;
    }
    setOptionsLoading(true);
    setOptionsError(null);
    try {
      const mode = isAdminContext ? 'admin' : resolveFetchMode();
      if (mode === 'admin') {
        const options = await publicMarketService.getAdminCoinOpenOptions(symbol);
        setEligibleStrategies(options.strategies ?? []);
        setDirectAccounts((options.directAccounts ?? []).filter(a => a.selectable));
        const amt = Number(options.defaultDirectAmount ?? 10);
        const lev = Number(options.defaultDirectLeverage ?? 5);
        setDefaultDirectAmount(amt);
        setDefaultDirectLeverage(lev);
        setDirectAmount(amt.toFixed(2));
        setDirectLeverage(String(lev));
        if ((options.strategies ?? []).length > 0) {
          setSelectedStrategyId(options.strategies[0].strategyId);
        } else {
          setSelectedStrategyId('');
        }
        const selectableAccounts = (options.directAccounts ?? []).filter(a => a.selectable);
        if (selectableAccounts.length > 0) {
          setSelectedKucoinConfigId(selectableAccounts[0].id);
          if (!defaultOpenMode && (options.strategies ?? []).length === 0) {
            setOpenMode('DIRECT');
          }
        } else {
          setSelectedKucoinConfigId('');
        }
        if (!defaultOpenMode) {
          if ((options.strategies ?? []).length > 0) setOpenMode('STRATEGY');
        } else {
          setOpenMode(defaultOpenMode);
        }
      } else if (mode === 'retail') {
        const options = await publicMarketService.getRetailCoinOpenOptions(symbol);
        setEligibleStrategies(options);
        setDirectAccounts([]);
        setOpenMode(defaultOpenMode ?? 'STRATEGY');
        if (options.length > 0) {
          setSelectedStrategyId(options[0].strategyId);
        } else {
          setSelectedStrategyId('');
        }
      } else {
        setEligibleStrategies([]);
        setDirectAccounts([]);
        setSelectedStrategyId('');
      }
    } catch (err: any) {
      const code = err?.message || '';
      if (code.includes('403') || code.includes('401')) {
        setOptionsError('You do not have permission to open positions from Market Signals.');
      } else {
        setOptionsError('Failed to load trading strategies for this coin.');
      }
      setEligibleStrategies([]);
      setDirectAccounts([]);
      setSelectedStrategyId('');
      setSelectedKucoinConfigId('');
    } finally {
      setOptionsLoading(false);
    }
  }, [canOpenPositions, isAdminContext, defaultOpenMode]);

  useEffect(() => {
    if (!open || !coin?.symbol) return;
    setSubmitError(null);
    setSubmitSuccess(null);
    setPositionsView('coin');
    setSide(coin.active ? 'LONG' : 'SHORT');
    if (defaultOpenMode) {
      setOpenMode(defaultOpenMode);
    }
    loadPositions(coin.symbol, 'coin');
    loadOpenOptions(coin.symbol);
  }, [open, coin?.symbol, coin?.active, defaultOpenMode, loadPositions, loadOpenOptions]);

  useEffect(() => {
    if (!open || !coin?.symbol) {
      setCoinProfile(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const profile = await publicMarketService.getCoinProfile(coin.symbol);
        if (!cancelled) setCoinProfile(profile);
      } catch {
        if (!cancelled) setCoinProfile(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, coin?.symbol]);

  const handlePositionsViewChange = (view: PositionsView) => {
    if (view === positionsView || !coin?.symbol) return;
    setPositionsView(view);
    loadPositions(coin.symbol, view);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const selectedStrategy = useMemo(
    () => eligibleStrategies.find(s => s.strategyId === selectedStrategyId),
    [eligibleStrategies, selectedStrategyId]
  );

  const selectedDirectAccount = useMemo(
    () => directAccounts.find(a => a.id === selectedKucoinConfigId),
    [directAccounts, selectedKucoinConfigId]
  );

  const canShowOpenForm = eligibleStrategies.length > 0 || (isAdminContext && directAccounts.length > 0);

  const MAX_DIRECT_LEVERAGE = 10;

  const canSubmitOpen =
    openMode === 'STRATEGY'
      ? Boolean(selectedStrategyId)
      : Boolean(
          selectedKucoinConfigId
          && Number(directAmount) > 0
          && Number(directLeverage) >= 1
          && Number(directLeverage) <= MAX_DIRECT_LEVERAGE,
        );

  const handleOpenPosition = async () => {
    if (!coin?.symbol || !side || !canSubmitOpen) return;
    const fetchMode = isAdminContext ? 'admin' : resolveFetchMode();

    if (openMode === 'DIRECT') {
      const lev = Number(directLeverage);
      if (!Number.isFinite(lev) || lev < 1 || lev > MAX_DIRECT_LEVERAGE) {
        setSubmitError(`Leverage must be between 1x and ${MAX_DIRECT_LEVERAGE}x.`);
        return;
      }
    }

    if (openMode === 'STRATEGY') {
      const strat = eligibleStrategies.find(s => s.strategyId === selectedStrategyId);
      const size = Number(strat?.fixedOrderAmount ?? 0).toFixed(2);
      const account = strat?.kucoinAccountName ?? 'KuCoin';
      const ok = window.confirm(
        `Open ${side} ${coin.symbol} on "${strat?.strategyName}"?\n\n` +
        `Size: $${size} @ ${strat?.leverage ?? '?'}x\n` +
        `Account: ${account}\n\n` +
        `This places a real FUTURES order on KuCoin.`
      );
      if (!ok) return;
    } else {
      const account = selectedDirectAccount?.name ?? 'KuCoin';
      const ok = window.confirm(
        `Open ${side} ${coin.symbol} directly (no trading bot)?\n\n` +
        `Size: $${Number(directAmount).toFixed(2)} @ ${directLeverage}x\n` +
        `Account: ${account}\n\n` +
        `This places a real FUTURES order on KuCoin.`
      );
      if (!ok) return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      const payload =
        openMode === 'STRATEGY'
          ? {
              symbol: coin.symbol,
              openMode: 'STRATEGY' as const,
              strategyId: Number(selectedStrategyId),
              side,
            }
          : {
              symbol: coin.symbol,
              openMode: 'DIRECT' as const,
              side,
              kucoinConfigId: Number(selectedKucoinConfigId),
              amount: Number(directAmount),
              leverage: Number(directLeverage),
            };

      const result: MarketOpenPositionResult =
        fetchMode === 'admin'
          ? await publicMarketService.openAdminCoinPosition(payload)
          : fetchMode === 'retail'
            ? await publicMarketService.openRetailCoinPosition(payload as MarketOpenPositionPayload & { strategyId: number })
            : { success: false, message: 'Not authenticated' };

      if (!result.success) {
        setSubmitError(result.message || 'Failed to open position');
        return;
      }
      setSubmitSuccess(result.message || `Opened ${result.side} on ${result.strategyName}`);
      await loadPositions(coin.symbol, positionsView);
    } catch (err: any) {
      setSubmitError(err?.body?.message || err?.message || 'Failed to open position');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClosePosition = async (row: MarketCoinPositionRow) => {
    const closeSymbol = row.symbol || coin?.symbol;
    if (!closeSymbol || !isAdminContext || !row.hedgePositionId) return;
    const ok = window.confirm(
      `Close ${row.side} ${closeSymbol} on "${row.strategyName}"?\n\n` +
      `This sends a real close order to KuCoin.`
    );
    if (!ok) return;

    setClosingHedgePositionId(row.hedgePositionId);
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      const result = await publicMarketService.closeAdminCoinPosition({
        symbol: closeSymbol,
        hedgePositionId: row.hedgePositionId,
      });
      if (!result.success) {
        setSubmitError(result.message || 'Failed to close position');
        return;
      }
      setSubmitSuccess(result.message || `Closed ${row.side} on ${closeSymbol}`);
      if (coin?.symbol) {
        await loadPositions(coin.symbol, positionsView);
      }
    } catch (err: any) {
      setSubmitError(err?.body?.message || err?.message || 'Failed to close position');
    } finally {
      setClosingHedgePositionId(null);
    }
  };

  const positions: MarketCoinPositionRow[] = data?.positions ?? [];

  const spotPrice = useMemo(() => {
    if (!coin?.symbol) return null;
    const profilePrice = coinProfile?.price;
    if (profilePrice != null && profilePrice > 0) return profilePrice;
    if (coin.price != null && coin.price > 0) return coin.price;
    const rows = data?.positions ?? [];
    const match = rows.find(
      p => p.symbol?.toUpperCase() === coin.symbol.toUpperCase() && Number(p.currentPrice) > 0
    );
    return match?.currentPrice ?? null;
  }, [coin?.price, coin?.symbol, coinProfile?.price, data?.positions]);

  if (!open || !coin || !displayCoin) return null;

  const sideBtn = (value: 'LONG' | 'SHORT', label: string) => {
    const active = side === value;
    return (
      <button
        type="button"
        onClick={() => setSide(value)}
        style={{
          flex: 1,
          padding: '8px 10px',
          borderRadius: 999,
          border: `1px solid ${active ? '#086afb' : '#e2e8f0'}`,
          background: active ? '#086afb' : '#fff',
          color: active ? '#fff' : '#64748b',
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        {label}
      </button>
    );
  };

  const viewBtn = (value: PositionsView, label: string) => {
    const active = positionsView === value;
    return (
      <button
        type="button"
        onClick={() => handlePositionsViewChange(value)}
        style={{
          flex: 1,
          padding: '6px 10px',
          borderRadius: 999,
          border: `1px solid ${active ? '#086afb' : '#e2e8f0'}`,
          background: active ? '#086afb' : '#fff',
          color: active ? '#fff' : '#64748b',
          fontSize: 11,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        {label}
      </button>
    );
  };

  const modeBtn = (value: MarketOpenMode, label: string) => {
    const active = openMode === value;
    return (
      <button
        type="button"
        onClick={() => setOpenMode(value)}
        style={{
          flex: 1,
          padding: '8px 10px',
          borderRadius: 999,
          border: `1px solid ${active ? '#086afb' : '#e2e8f0'}`,
          background: active ? '#086afb' : '#fff',
          color: active ? '#fff' : '#64748b',
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <>
      <div
        role="presentation"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.45)',
          zIndex: 1200,
        }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="coin-positions-drawer-title"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100%',
          width: 'min(420px, 100vw)',
          background: '#fff',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
          zIndex: 1201,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'Nunito, system-ui, sans-serif',
        }}
      >
        <div
          style={{
            padding: '20px 20px 16px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div>
            <div id="coin-positions-drawer-title" style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>
              {displayCoin.symbol}
            </div>
            {displayCoin.displayName && displayCoin.displayName !== displayCoin.symbol && (
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#475569',
                  marginTop: 4,
                  lineHeight: 1.35,
                }}
              >
                {displayCoin.displayName}
              </div>
            )}
            {(displayCoin.assetTypeLabel || displayCoin.sectorLabel) && (
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, lineHeight: 1.4 }}>
                {[displayCoin.assetTypeLabel, displayCoin.sectorLabel].filter(Boolean).join(' · ')}
              </div>
            )}
            {displayCoin.futuresSymbol && (
              <div
                style={{
                  fontSize: 11,
                  color: '#cbd5e1',
                  marginTop: 2,
                  fontFamily: 'Roboto Mono, ui-monospace, monospace',
                  letterSpacing: '0.02em',
                }}
              >
                {displayCoin.futuresSymbol}
              </div>
            )}
            <div
              style={{
                fontSize: 26,
                fontWeight: 800,
                color: '#0f172a',
                marginTop: 10,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              {fmtSpotPrice(spotPrice)}
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 8 }}>
              {canOpenPositions ? 'Open position & view bot legs' : 'Open bot positions'}
              {data
                ? positionsView === 'all'
                  ? ` · ${data.totalOpen} open (all coins)`
                  : ` · ${data.totalOpen} open`
                : ''}
            </div>
            <div style={{ fontSize: 12, marginTop: 6, color: displayCoin.change24h >= 0 ? '#15803d' : '#b91c1c', fontWeight: 600 }}>
              24h {displayCoin.change24h >= 0 ? '+' : ''}{displayCoin.change24h.toFixed(2)}%
              <span style={{ color: '#94a3b8', fontWeight: 500, marginLeft: 8 }}>
                {displayCoin.active ? 'HOT' : 'COOL'}
              </span>
              {(displayCoin.turnover24h ?? 0) > 0 && (
                <span style={{ color: '#94a3b8', fontWeight: 500, marginLeft: 8 }}>
                  Vol {fmtUsdCompact(displayCoin.turnover24h)}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: 'none',
              background: '#f1f5f9',
              borderRadius: 8,
              width: 32,
              height: 32,
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              color: '#475569',
            }}
          >
            ×
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 24px' }}>
          {displayCoin && (
            <CoinActivityChart
              symbol={displayCoin.symbol}
              change24h={displayCoin.change24h}
              isAdminContext={isAdminContext}
              hours={24}
            />
          )}
          {canOpenPositions && (
            <div
              style={{
                border: '2px solid #086afb',
                borderRadius: 12,
                padding: '14px 16px',
                marginBottom: 16,
                background: 'linear-gradient(180deg, #f0f7ff 0%, #f8fafc 100%)',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 10 }}>
                Open new position
              </div>
              <p style={{ margin: '0 0 12px', fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                Open via one of your trading bots, or directly on your KuCoin account without linking a bot.
              </p>

              {isAdminContext && directAccounts.length > 0 && eligibleStrategies.length > 0 && (
                <>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>
                    Open via
                  </label>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    {modeBtn('STRATEGY', 'Trading bot')}
                    {modeBtn('DIRECT', 'My KuCoin')}
                  </div>
                </>
              )}

              {isAdminContext && directAccounts.length > 0 && eligibleStrategies.length === 0 && (
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
                  No eligible bots for this coin — opening directly on your KuCoin account.
                </div>
              )}

              {optionsLoading && (
                <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>Loading active FUTURES bots…</p>
              )}

              {optionsError && !optionsLoading && (
                <div role="alert" style={{ fontSize: 12, color: '#b91c1c', marginBottom: 8 }}>{optionsError}</div>
              )}

              {!optionsLoading && !optionsError && !canShowOpenForm && (
                <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
                  <p style={{ margin: '0 0 8px' }}>
                    No <strong>ACTIVE FUTURES</strong> bots or KuCoin accounts are available for <strong>{coin.symbol}</strong> yet.
                  </p>
                  <ul style={{ margin: '0 0 8px', paddingLeft: 18 }}>
                    <li>Start or create a meme/major-coin <strong>FUTURES</strong> bot in Admin → Trading Bots</li>
                    <li>Or add a personal KuCoin account under Admin → My Trading Accounts</li>
                    <li>Publish the bot for retail users (if opening from retail)</li>
                  </ul>
                </div>
              )}

              {!optionsLoading && canShowOpenForm && openMode === 'STRATEGY' && eligibleStrategies.length > 0 && (
                <>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 4 }}>
                    Trading bot
                  </label>
                  <select
                    value={selectedStrategyId}
                    onChange={(e) => setSelectedStrategyId(e.target.value ? Number(e.target.value) : '')}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: '1px solid #e2e8f0',
                      fontSize: 13,
                      marginBottom: 10,
                    }}
                  >
                    {eligibleStrategies.map(s => (
                      <option key={s.strategyId} value={s.strategyId}>
                        {s.strategyName}
                        {s.hedgeMode ? ' · hedge' : ' · pair'}
                        {s.paperTrading ? ' · paper' : ''}
                      </option>
                    ))}
                  </select>

                  {selectedStrategy && (
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12, lineHeight: 1.5 }}>
                      KuCoin: <strong>{selectedStrategy.kucoinAccountName}</strong>
                      {' · '}${Number(selectedStrategy.fixedOrderAmount ?? 0).toFixed(2)} @ {selectedStrategy.leverage}x
                      {selectedStrategy.hedgeMode ? ' · hedge mode' : ' · pair mode'}
                    </div>
                  )}
                </>
              )}

              {!optionsLoading && canShowOpenForm && openMode === 'DIRECT' && directAccounts.length > 0 && (
                <>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 4 }}>
                    KuCoin account
                  </label>
                  <select
                    value={selectedKucoinConfigId}
                    onChange={(e) => setSelectedKucoinConfigId(e.target.value ? Number(e.target.value) : '')}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: '1px solid #e2e8f0',
                      fontSize: 13,
                      marginBottom: 10,
                    }}
                  >
                    {directAccounts.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name}{a.shared ? ' (shared)' : ''}
                      </option>
                    ))}
                  </select>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 4 }}>
                        Size (USD)
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="0.01"
                        value={directAmount}
                        onChange={(e) => setDirectAmount(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: 8,
                          border: '1px solid #e2e8f0',
                          fontSize: 13,
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 4 }}>
                        Leverage
                      </label>
                      <input
                        type="number"
                        min="1"
                        max={MAX_DIRECT_LEVERAGE}
                        step="1"
                        value={directLeverage}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            setDirectLeverage(raw);
                            return;
                          }
                          const next = Number(raw);
                          if (!Number.isFinite(next)) {
                            setDirectLeverage(raw);
                            return;
                          }
                          setDirectLeverage(String(Math.max(1, Math.min(MAX_DIRECT_LEVERAGE, Math.round(next)))));
                        }}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: 8,
                          border: '1px solid #e2e8f0',
                          fontSize: 13,
                        }}
                      />
                    </div>
                  </div>
                  <p style={{ margin: '0 0 12px', fontSize: 11, color: '#64748b' }}>
                    Not linked to any trading bot. Defaults: ${defaultDirectAmount.toFixed(2)} @ {defaultDirectLeverage}x. Max leverage {MAX_DIRECT_LEVERAGE}x.
                  </p>
                </>
              )}

              {!optionsLoading && canShowOpenForm && (
                <>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>
                    Side
                    <span style={{ fontWeight: 500, color: '#94a3b8', marginLeft: 6 }}>
                      {coin.active ? '(HOT → often LONG)' : '(COOL → often SHORT)'}
                    </span>
                  </label>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    {sideBtn('LONG', 'LONG')}
                    {sideBtn('SHORT', 'SHORT')}
                  </div>

                  {submitSuccess && (
                    <div
                      role="status"
                      style={{
                        padding: '10px 12px',
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: 8,
                        color: '#15803d',
                        fontSize: 12,
                        marginBottom: 10,
                      }}
                    >
                      {submitSuccess}
                    </div>
                  )}

                  {submitError && (
                    <div role="alert" style={{ fontSize: 12, color: '#b91c1c', marginBottom: 10 }}>{submitError}</div>
                  )}

                  <button
                    type="button"
                    onClick={handleOpenPosition}
                    disabled={submitting || !canSubmitOpen}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 999,
                      border: 'none',
                      background: submitting ? '#94a3b8' : '#e7038e',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: submitting ? 'wait' : 'pointer',
                    }}
                  >
                    {submitting ? 'Opening…' : `Open ${side} on ${coin.symbol}`}
                  </button>
                </>
              )}
            </div>
          )}

          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
              <svg className="animate-spin" style={{ width: 32, height: 32, color: '#94a3b8' }} fill="none" viewBox="0 0 24 24">
                <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          )}

          {error && !loading && (
            <div
              role="alert"
              style={{
                padding: '12px 14px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 10,
                color: '#b91c1c',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>
                  Current open positions
                </div>
                <div style={{ display: 'flex', gap: 6, minWidth: 180 }}>
                  {viewBtn('coin', coin.symbol)}
                  {viewBtn('all', 'All coins')}
                </div>
              </div>

              {positions.length === 0 && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '24px 12px',
                    color: '#64748b',
                    fontSize: 13,
                  }}
                >
                  {positionsView === 'all'
                    ? 'No open positions across your accounts yet.'
                    : `No open positions for ${coin.symbol} yet.`}
                </div>
              )}

              {positions.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {positions.map((row, idx) => {
                    const pnl = Number(row.unrealizedPnl ?? 0);
                    const pnlPositive = pnl >= 0;
                    return (
                      <div
                        key={`${row.strategyId ?? 'direct'}-${row.side}-${idx}`}
                        style={{
                          border: '1px solid #e2e8f0',
                          borderRadius: 12,
                          padding: '14px 16px',
                          background: '#fafafa',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{row.strategyName}</div>
                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                              {row.side} · {row.symbol}
                            </div>
                          </div>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              padding: '2px 8px',
                              borderRadius: 999,
                              background: row.side === 'LONG' ? '#dcfce7' : '#fee2e2',
                              color: row.side === 'LONG' ? '#15803d' : '#b91c1c',
                            }}
                          >
                            {row.side}
                          </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px', marginTop: 12, fontSize: 12 }}>
                          <div>
                            <div style={{ color: '#94a3b8' }}>Entry</div>
                            <div style={{ fontWeight: 600, color: '#334155' }}>{fmtPrice(row.entryPrice)}</div>
                          </div>
                          <div>
                            <div style={{ color: '#94a3b8' }}>Current</div>
                            <div style={{ fontWeight: 600, color: '#334155' }}>{fmtPrice(row.currentPrice)}</div>
                          </div>
                          <div>
                            <div style={{ color: '#94a3b8' }}>Size</div>
                            <div style={{ fontWeight: 600, color: '#334155' }}>${Number(row.amount ?? 0).toFixed(2)}</div>
                          </div>
                          <div>
                            <div style={{ color: '#94a3b8' }}>Unrealized P&amp;L</div>
                            <div style={{ fontWeight: 700, color: pnlPositive ? '#15803d' : '#b91c1c' }}>
                              {fmtPnl(pnl)}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                          <button
                            type="button"
                            onClick={() => row.strategyId != null && onViewStrategy(row.strategyId, row.symbol)}
                            disabled={row.strategyId == null}
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              borderRadius: 999,
                              border: 'none',
                              background: row.strategyId == null ? '#cbd5e1' : '#086afb',
                              color: '#fff',
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: row.strategyId == null ? 'default' : 'pointer',
                            }}
                          >
                            {row.strategyId == null ? 'Direct position (no bot)' : 'View strategy'}
                          </button>
                          {isAdminContext && canOpenPositions && row.hedgePositionId != null && (
                            <button
                              type="button"
                              onClick={() => handleClosePosition(row)}
                              disabled={closingHedgePositionId === row.hedgePositionId}
                              style={{
                                flex: 1,
                                padding: '8px 12px',
                                borderRadius: 999,
                                border: 'none',
                                background: closingHedgePositionId === row.hedgePositionId ? '#94a3b8' : '#ef4444',
                                color: '#fff',
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: closingHedgePositionId === row.hedgePositionId ? 'wait' : 'pointer',
                              }}
                            >
                              {closingHedgePositionId === row.hedgePositionId ? 'Closing…' : 'Close'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
};

export default CoinPositionsDrawer;
